// The accounting state of a document or bank movement in the ledger account reconciliation,
// shared by the list (the concepts in red, the movements' status dot) and the accounting
// entries panel (its buttons and status bar): no journal entry yet, a draft entry, or a locked
// one. The panel posts the change notification whenever it generates, locks, unlocks or
// discards an entry, so the list follows.
//
// Before the entries were made per invoice, the reconciliation booked sales invoices line by
// line: those lines carry their own journal entry and lock. An invoice with such lines and no
// entry of its own counts as booked by them (see stateForDocument).

enum ReconciliationEntryState
{
    none = 0,
    draft = 1,
    locked = 2
}

// The status filter of the reconciliation list (the values of its combobox)
enum ReconciliationStatusFilter
{
    all = "all",
    pending = "pending",
    draft = "draft",
    locked = "locked"
}

class ReconciliationEntryStateHelper
{
    /// Posted with the document or movement whose journal entry the panel changed.
    static readonly didChangeNotification = "ReconciliationEntryStateDidChange";

    /// The amounts are rounded to cents: anything under half a cent is balanced (as the server).
    static readonly balanceTolerance = 0.005;

    static stateForItem(item:CoreAccounting) : ReconciliationEntryState {
        if (item == null) return ReconciliationEntryState.none;
        if (item.journalEntryLocked == true || item.journalEntry?.locked == true) return ReconciliationEntryState.locked;
        if (item.journalEntry != null) return ReconciliationEntryState.draft;
        return ReconciliationEntryState.none;
    }

    static isLocked(item:CoreAccounting) : boolean {
        return ReconciliationEntryStateHelper.stateForItem(item) == ReconciliationEntryState.locked;
    }

    /// An invoice's state, counting the lines booked one by one when it has no entry of its own:
    /// locked while any of them is (it is unlocked before anything replaces it), a draft otherwise.
    static stateForDocument(document:CoreAccounting, lines:CoreAccounting[]) : ReconciliationEntryState {
        let state = ReconciliationEntryStateHelper.stateForItem(document);
        if (state != ReconciliationEntryState.none || document?.journalEntry != null) return state;

        let booked = ReconciliationEntryStateHelper.linesBookedOneByOne(document, lines);
        if (booked.length == 0) return ReconciliationEntryState.none;
        let anyLocked = booked.some(l => ReconciliationEntryStateHelper.stateForItem(l) == ReconciliationEntryState.locked);
        return anyLocked ? ReconciliationEntryState.locked : ReconciliationEntryState.draft;
    }

    /// The lines of an invoice without entry of its own that have one (see the top).
    static linesBookedOneByOne(document:CoreAccounting, lines:CoreAccounting[]) : CoreAccounting[] {
        if (document == null || document.journalEntry != null) return [];
        return (lines ?? []).filter(l => l != null && l.journalEntry != null);
    }

    static isBalanced(difference:number) : boolean {
        return Math.abs(difference ?? 0) < ReconciliationEntryStateHelper.balanceTolerance;
    }

    /// The manager's status style of the list's dot: red without entry, yellow while it is a
    /// draft to review, green once it is locked.
    static styleForState(state:ReconciliationEntryState) : string {
        switch (state) {
            case ReconciliationEntryState.locked: return "active";
            case ReconciliationEntryState.draft:  return "caution";
            default:                              return "alert";
        }
    }

    static titleForState(state:ReconciliationEntryState) : string {
        switch (state) {
            case ReconciliationEntryState.locked: return MIOLocalizeString("CORRECT_AND_LOCKED", "Correct and locked");
            case ReconciliationEntryState.draft:  return MIOLocalizeString("PENDING_REVIEW", "Pending review");
            default:                              return MIOLocalizeString("MISSING_DATA", "Missing data");
        }
    }

    /// Puts the state's style on the dot, taking the other two off, with its name as the tooltip.
    static applyStateToDot(layer:HTMLElement, state:ReconciliationEntryState) {
        if (layer == null) return;
        for (let style of ["alert", "caution", "active"]) layer.classList.remove(style);
        layer.classList.add(ReconciliationEntryStateHelper.styleForState(state));
        layer.title = ReconciliationEntryStateHelper.titleForState(state);
    }

    /// The predicate of the status filter, the same rules as stateForItem: on the item itself, or
    /// on an invoice's lines through the "document." prefix (the lines booked one by one then count
    /// too). Null when every item is shown.
    static predicateFormatForFilter(filter:ReconciliationStatusFilter, documentPrefix:string = "") : string {
        let p = documentPrefix;
        let lines = p.length > 0;
        let locked = "(" + p + "journalEntryLocked == true OR " + p + "journalEntry.locked == true)";
        let draft = p + "journalEntry != null AND " + p + "journalEntryLocked == false AND " + p + "journalEntry.locked == false";
        let noEntry = p + "journalEntry == null";

        switch (filter) {
            case ReconciliationStatusFilter.pending:
                return lines ? noEntry + " AND journalEntry == null" : noEntry;

            case ReconciliationStatusFilter.draft:
                if (!lines) return draft;
                return "(" + draft + ") OR (" + noEntry + " AND journalEntry != null AND journalEntryLocked == false AND journalEntry.locked == false)";

            case ReconciliationStatusFilter.locked:
                if (!lines) return locked;
                return locked + " OR (" + noEntry + " AND journalEntry != null AND (journalEntryLocked == true OR journalEntry.locked == true))";

            default:
                return null;
        }
    }

    /// Labels render HTML; names typed by users are shown as text.
    static escape(text:any) : string {
        return String(text ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    }

    /// "705.0.0 · Services", escaped for a dropdown title or a label.
    static accountTitle(account:LedgerAccount) : string {
        if (account == null) return null;
        let prefix = account.prefix ?? "";
        let name = account.name ?? "";
        if (prefix.length == 0) return ReconciliationEntryStateHelper.escape(name);
        if (name.length == 0) return ReconciliationEntryStateHelper.escape(prefix);
        return ReconciliationEntryStateHelper.escape(prefix + " · " + name);
    }

    /// The legal entity (customer or supplier) of an invoice, or null.
    static legalEntityOfDocument(document:any) : LegalEntity {
        return (document?.legalEntity ?? null) as LegalEntity;
    }

    /// The reference the server stamps on the generated lines of a document (documentReference in
    /// AccountingJournalCalculator.swift): on a purchase invoice the supplier's own number, else
    /// our document number. A line added by hand takes the same one.
    static referenceOfDocument(document:any) : string {
        if (document instanceof PurchaseInvoice) {
            let reference = document.externalReference;
            if (reference?.length > 0) return reference;
        }
        return document?.documentNumber ?? null;
    }

    /// The accounts of a customer or supplier: an entry line on one of them is shown with the
    /// party's name instead of the account's.
    static partyAccountsOfLegalEntity(legalEntity:LegalEntity) : LedgerAccount[] {
        if (legalEntity == null) return [];
        let le = legalEntity as any;
        return [le.salesLedgerAccount, le.purchaseLedgerAccount, le.expenditureLedgerAccount, le.ledgerAccount].filter(a => a != null);
    }

    /// The customers' and suppliers' groups of the chart of accounts (40, 41, 43).
    static isPartyAccountNumber(prefix:string) : boolean {
        if (prefix == null) return false;
        return prefix.startsWith("40") || prefix.startsWith("41") || prefix.startsWith("43");
    }
}
